import useScrollBottom from 'app/hooks/useScrollBottom';
import { Box } from '@mui/system';
import { Fade, Paper } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from 'app/store';
import { setCurrentPage, useNavigationState } from 'app/store/navigationSlice';
import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { determineEnvironment } from 'app/pages/onboard/utils';

interface PageProps {
  id: string;
  Header?: React.ReactNode;
  NavigationTabs?: React.ReactNode;
  onScrollToBottom?: (isBottom: boolean) => void;
  children?: React.ReactNode;
  margins?: boolean;
}

const Page = forwardRef(function (
  { id, Header, NavigationTabs, onScrollToBottom, children, margins = false }: PageProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const environment = determineEnvironment();
  const dispatch = useAppDispatch();
  const { drawerOpen } = useNavigationState();
  const [fadeIn, setFadeIn] = useState(false);
  const [isBottom, scrollRef] = useScrollBottom({ offset: 100, ref });
  const lastBasePathRef = useRef('');

  const location = useLocation();
  const isReviewPage = location.pathname.match(/^\/review/);
  const isOnboardPage = location.pathname.match(/^\/onboard/);
  const reducePageMargin = isReviewPage || isOnboardPage;

  useEffect(() => {
    setFadeIn(true);
    dispatch(setCurrentPage(id));

    return () => {
      setFadeIn(false);
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (onScrollToBottom) {
      onScrollToBottom(isBottom);
    }
  }, [isBottom, onScrollToBottom]);

  useLayoutEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const baseRoute = segments.slice(0, 2).join('/');

    if (baseRoute !== lastBasePathRef.current) {
      window.scrollTo(0, 0);
      lastBasePathRef.current = baseRoute;
    }
  }, [location.pathname]);

  return (
    <Fade in={fadeIn}>
      <Box
        ref={scrollRef}
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflow: 'auto',
          pl: reducePageMargin ? { md: 2 } : { md: 1 },
          pt: environment === 'production' ? '24px' : '85px',
          ml: { xs: 0, sm: reducePageMargin ? 0 : drawerOpen ? '200px' : '72px' },
        }}
      >
        {Header}
        <Paper
          elevation={0}
          sx={{
            flexGrow: 1,
            borderRadius: { md: 4 },
            mt: { md: Header ? undefined : 2 },
            mr: { md: 2 },
            mb: { md: 2 },
            boxShadow: { md: '5px 5px 20px 0px #00000040' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {NavigationTabs}
          <Box
            sx={{
              p: margins ? 2 : 0,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {children}
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
});

export default Page;
